import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import Login from '@/views/loginMgt/logPage/index.vue'
import emptyPage from '@/views/loginMgt/emptyPage/index.vue'
import registerPage from '@/views/loginMgt/registerPage/index.vue'
import resetPage from '@/views/loginMgt/resetPage/index.vue'
import Layout from '@/components/layout/defaultTheme.vue' // 布局组件
import linkPhone from '@/views/userMgt/equipmentPage/conponents/linkPhone.vue'
import { getMenus } from '@/utils/storeMenu.ts'
import { ElMessage } from 'element-plus'
import NotFound from '@/views/loginMgt/notFoundPage/index.vue'
import { loadRoutesFromBackend } from './dynamicRoutes'
import appStoreMgt from '@/views/productMgt/appStoreMgt/index.vue'
import getDevice from '@/h5/getDevice/index.vue'
import workPlace from '@/h5/workPlace/index.vue'
import getExchangeCode from '@/h5/getExchangeCode/index.vue'
import allocateDevice from '@/h5/allocateDevice/index.vue'
import configSkuStock from '@/h5/configSkuStock/index.vue'
import userDetail from '@/h5/userDetail/index.vue'
import exchangeCodeOrderMgt from '@/views/exchangeMgt/exchangeCodeOrderMgt/index.vue'
import exchangeCodeProduct from '@/views/exchangeMgt/exchangeCodeProduct/index.vue'

// 定义静态路由（不需要权限验证的路由）
const staticRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/login', // 默认重定向到登录页
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/register',
    name: 'register',
    component: registerPage,
  },
  {
    path: '/reset',
    name: 'resetPage',
    component: resetPage,
  },
  {
    path: '/cloudPhone/:code',
    name: 'cloudLink',
    component: linkPhone,
  },
  {
    path: '/404',
    name: 'notFound',
    component: NotFound,
  },
  {
    path: '/h5/getDevice',
    name: 'getDevice',
    component: getDevice,
  },
  {
    path: '/h5/workPlace',
    name: 'workPlace',
    component: workPlace,
  },
  {
    path: '/h5/exchangeCode',
    name: 'exchangeCode',
    component: getExchangeCode,
  },
  {
    path: '/h5/allocateDevice',
    name: 'allocateDevice',
    component: allocateDevice,
  },
  {
    path: '/h5/configSkuStock',
    name: 'configSkuStock',
    component: configSkuStock,
  },
  {
    path: '/h5/userDetail',
    name: 'userDetail',
    component: userDetail,
  },
  {
    path: '/appStoreMgt',
    name: 'appStoreMgt',
    component: appStoreMgt,
  },
  {
    path: '/exchangeCodeOrderMgt',
    name: 'exchangeCodeOrderMgt',
    component: exchangeCodeOrderMgt,
  },
  {
    path: '/exchangeCodeProduct',
    name: 'exchangeCodeProduct',
    component: exchangeCodeProduct,
  },
  {
    path: '/',
    name: '空页面1',
    component: Layout,
    children: [
      {
        path: '_blank',
        name: '空页面',
        component: emptyPage,
        meta: { title: '_blank' },
      },
    ],
  },
  // 根节点路由，用于容纳动态加载的菜单路由
  {
    path: '/root',
    name: 'SAAS',
    meta: {
      title: 'SAAS',
      icon: 'Menu',
    },
    children: [],
  },
]

// 创建路由实例
export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: staticRoutes,
})

// 记录已添加的动态路由 name，便于干净地移除后重建（避免重复/残留路由）。
// 不再依赖 getRoutes() 返回的 children 结构（该结构在不同场景下可能为空，导致移除失败）。
let addedRouteNames: (string | symbol)[] = []
// 动态路由是否已按“当前菜单”加载完成。
// resetDynamicRoutes() 会置回 false（切租户/角色、菜单更新时），路由守卫据此判断是否需要重建，
// 替代原先靠 getRoutes() 路径前缀判断的做法（动态子路由是绝对路径，永远不以 '/root/' 开头，会误判为“未加载”导致无限重建卡死）。
let dynamicRoutesLoaded = false

/**
 * 递归收集路由树中所有节点的 name
 */
function collectRouteNames(routes: any[]): (string | symbol)[] {
  const names: (string | symbol)[] = []
  const walk = (list: any[]) => {
    list.forEach((r) => {
      if (r.name) names.push(r.name)
      if (r.children?.length) walk(r.children)
    })
  }
  walk(routes || [])
  return names
}

/**
 * 重置动态路由状态，允许重新加载
 */
export function resetDynamicRoutes() {
  addedRouteNames.forEach((name) => {
    // 仅当路由表中确实存在该 name 时才移除，避免对不存在的路由调用 removeRoute
    if (router.hasRoute(name)) {
      router.removeRoute(name)
    }
  })
  addedRouteNames = []
  dynamicRoutesLoaded = false
}
/**
 * 加载动态路由
 * 通常在用户登录后调用此函数。
 * 幂等：每次调用都会先移除旧动态路由再按最新菜单重建，
 * 这样切换租户/角色后菜单变化能被正确反映，不再被某个标志位锁死。
 */
export async function initDynamicRoutes() {
  try {
    const dynamicRoutes = await loadRoutesFromBackend(Layout)
    // 移除已有的动态路由（防止重复添加）
    resetDynamicRoutes()

    // 添加动态路由
    dynamicRoutes.forEach((route: any) => {
      router.addRoute('SAAS', route)
    })
    addedRouteNames = collectRouteNames(dynamicRoutes)
    // 仅当本地确有菜单时才标记“已加载”：登录前无菜单的预加载不能把标志位锁死，
    // 否则登录后写入菜单、跳转首个菜单页时守卫会跳过重建 → 白屏。
    dynamicRoutesLoaded = !!getMenus()
    return true
  } catch (error) {
    ElMessage.error('加载菜单失败，请刷新页面重试')
    return false
  }
}

// 屏蔽路由 - 不需要权限验证的路由
const filterRouter = [
  '/404',
  '/login',
  '/register',
  '/cloudPhone',
  '/reset',
  '/_blank',
  '/h5/userDetail',
  '/h5/getDevice',
  '/h5/workPlace',
  '/h5/exchangeCode',
  '/h5/allocateDevice',
  '/h5/configSkuStock',
  '',
]

// 全局前置守卫
router.beforeEach(async (to, from, next) => {
  // 判断目标路由在不在菜单列表中
  const menus = getMenus()
  // 不需要权限验证的路由直接通过
  // 注意用 startsWith('/cloudPhone/') 精确匹配云机查看器路由 /cloudPhone/:code；
  // 不能用 includes('/cloudPhone') —— /productMgt/cloudPhoneCapture 也含该子串，
  // 会导致进该页时跳过动态路由加载、命中未注册路由 → 整页空白（首次进入必现，F5 因启动预加载而正常）。
  if (filterRouter.includes(to.path) || to.path.startsWith('/cloudPhone/')) {
    next()
    return
  }

  // 检查是否已加载菜单数据
  if (!menus) {
    // 如果没有菜单数据，重定向到登录页
    next('/login')
    return
  }

  // 2. 动态路由还没加载就补一次（F5 刷新 / 直链进入 / 登录后首次进入场景）
  // 用 dynamicRoutesLoaded 标志判断；切租户/角色/菜单更新会经 resetDynamicRoutes() 置回 false 触发重建。
  if (!dynamicRoutesLoaded) {
    const ok = await initDynamicRoutes()
    if (!ok) return next('/login')
    // 重新进入目标路由，确保动态路由已生效
    return next({ ...to, replace: true })
  }

  // 检查用户是否有权限访问目标路由
  try {
    const menuData = typeof menus === 'string' ? JSON.parse(menus) : menus
    const flattenedMenus = flattenMenuTree(menuData)
    if (!to.name || !flattenedMenus.includes(String(to.name))) {
      // 重置并重新加载动态路由（切换角色/租户后路由可能已过期）
      resetDynamicRoutes()
      const ok = await initDynamicRoutes()
      if (ok) {
        // 检查目标路由是否已加载
        const routeExists = to.name && router.hasRoute(to.name)
        if (routeExists) {
          next({ ...to, replace: true })
        } else {
          // 跳转到当前角色的第一个菜单页面
          const firstName = getFirstMenuName(menuData)
          if (firstName) {
            next({ name: firstName, replace: true })
          } else {
            next('/404')
          }
        }
      } else {
        next('/404')
      }
      return
    }
  } catch (error) {
    console.error('解析菜单数据失败:', error)
    next('/login')
    return
  }
  next() // 路由没有权限要求，允许访问
})

/**
 * 扁平化菜单树，用于权限验证
 */
function flattenMenuTree(menuTree: any[]): string[] {
  let result: string[] = []

  if (!Array.isArray(menuTree)) {
    return result
  }

  menuTree.forEach((menu) => {
    if (menu.name) {
      result.push(menu.name)
    }

    if (menu.children && Array.isArray(menu.children)) {
      result = result.concat(flattenMenuTree(menu.children))
    }
  })

  return result
}

/**
 * 获取菜单树中第一个叶子节点的name，用于默认跳转
 */
function getFirstMenuName(menuTree: any[]): string | null {
  if (!Array.isArray(menuTree) || menuTree.length === 0) return null
  for (const menu of menuTree) {
    if (menu.children && menu.children.length > 0) {
      const result = getFirstMenuName(menu.children)
      if (result) return result
    } else if (menu.name) {
      return menu.name
    }
  }
  return null
}

export default router
